/**
 * ExpensesPanel — vendor-API observability / spend tracking for Digitap.ai.
 *
 * @origin: v2 | @since: 0570ba2d | @used-by: ALL
 * Ports v2's `frontend/src/pagesadmin/ExpensesPanel.tsx` (v2 branch
 * `v2/uat/digitap_monitor`, commit 0570ba2d "digitap monitor"), re-authored
 * against v3's backend contract.
 *
 * Backend (authoritative, already shipped — NOT modified by this port):
 *   GET /api/admin/digitap_logs/  ->  backend/los_app/admin_views.py::
 *   admin_digitap_logs_list, routed in backend/los_app/urls.py. SUPERUSER ONLY.
 *
 * The name `ExpensesPanel` is kept deliberately, for three reasons: v2's
 * sidebar label, `AdminView` id and file name all say "Expenses"; the backend
 * comments that came with the v3 endpoint call this screen "the Expenses
 * panel"; and Digitap bills per call, so call volume IS the spend figure this
 * screen reports. (Recon Open Question 12 notes there is no per-call price
 * anywhere in the range, so no currency column is rendered — the sub-screen is
 * titled "Digitap API Logs" to keep that honest.)
 *
 * DELIBERATE DIVERGENCES FROM v2 (each one a fix, not a preference):
 *  1. Filter dropdowns are built from the response's `choices` block. v2
 *     hardcoded the five api_type and three outcome values in TSX, so v3's
 *     wider Digitap surface would silently render an incomplete filter list.
 *  2. Payload blobs are fetched ON DEMAND for one row (`include_payloads=1`
 *     scoped by `user_id`), never for the list. v2 received both jsonb blobs in
 *     every row of every page, i.e. bulk PII on screen load.
 *  3. Summary tiles from `aggregates` (v2 returned no aggregates at all) — the
 *     actual point of a spend dashboard.
 *  4. `created_from`/`created_to` range + debounced `search`; v2 had an "Apply
 *     Filters" button and no date filtering.
 *  5. 403 / 400 / network / empty are four distinct rendered states. v2 dumped
 *     every failure into one red bar and showed "No records found" for a 403,
 *     which reads as "we made no Digitap calls" — the opposite of the truth.
 *
 * PII POSTURE: rows carry phone-number usernames; expanded payloads carry raw
 * mobile numbers and PANs. Nothing here is ever written to console,
 * localStorage or sessionStorage; payloads live in component state only and are
 * dropped when the row collapses or the query changes.
 */
import React from "react";
import {
    Activity,
    AlertTriangle,
    ArrowLeft,
    ChevronDown,
    ChevronRight,
    ChevronUp,
    Eye,
    Loader2,
    RefreshCw,
    ShieldAlert,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    adminService,
    getAdminApiErrorMessage,
    getAdminApiErrorStatus,
    isAdminApiCanceled,
    isAdminApiNetworkError,
    type DigitapChoice,
    type DigitapLogRow,
    type DigitapLogsAggregates,
    type DigitapLogsChoices,
    type DigitapLogsResponse,
} from "./adminApi";

// ── Constants ────────────────────────────────────────────────────────────────

/** Sentinel for "no filter"; Radix Select forbids an empty-string item value. */
const ALL = "__all__";

const EM_DASH = "—";

/**
 * Mirrors DIGITAP_LOGS_MAX_PAGE_SIZE in backend/los_app/admin_views.py. Used
 * only as the fallback when a response omits `max_page_size`; the server's own
 * value always wins, and the server clamps regardless of what is sent.
 */
const FALLBACK_MAX_PAGE_SIZE = 100;

const PAGE_SIZE_OPTIONS = [20, 50, 100];

/** Debounce for the free-text search box, so typing does not hammer the API. */
const SEARCH_DEBOUNCE_MS = 400;

// ── Defensive shape helpers ──────────────────────────────────────────────────
//
// The panel must not crash the whole admin UI if a response arrives without
// `aggregates`/`choices` (an older backend, a proxy rewriting the body, a
// partial deploy). Every read of those blocks goes through these.

const EMPTY_AGGREGATES: DigitapLogsAggregates = {
    total: 0,
    by_api_type: {},
    by_outcome: {},
    by_api_type_outcome: {},
};

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** Keeps only the `{value,label}` entries that are actually usable. */
function safeChoices(input: DigitapChoice[] | undefined): DigitapChoice[] {
    if (!Array.isArray(input)) return [];
    return input.filter(
        (c): c is DigitapChoice =>
            isRecord(c) && typeof c.value === "string" && c.value.length > 0,
    );
}

function safeCounts(input: Record<string, number> | undefined): Record<string, number> {
    if (!isRecord(input)) return {};
    const out: Record<string, number> = {};
    for (const [key, value] of Object.entries(input)) {
        if (typeof value === "number" && Number.isFinite(value)) out[key] = value;
    }
    return out;
}

function normaliseAggregates(
    input: DigitapLogsAggregates | undefined,
): DigitapLogsAggregates {
    if (!isRecord(input)) return EMPTY_AGGREGATES;
    const matrix: Record<string, Record<string, number>> = {};
    if (isRecord(input.by_api_type_outcome)) {
        for (const [apiType, cells] of Object.entries(input.by_api_type_outcome)) {
            matrix[apiType] = safeCounts(cells as Record<string, number>);
        }
    }
    return {
        total: typeof input.total === "number" ? input.total : 0,
        by_api_type: safeCounts(input.by_api_type),
        by_outcome: safeCounts(input.by_outcome),
        by_api_type_outcome: matrix,
    };
}

function normaliseChoices(input: DigitapLogsChoices | undefined): DigitapLogsChoices {
    if (!isRecord(input)) return { api_type: [], outcome: [] };
    return {
        api_type: safeChoices(input.api_type),
        outcome: safeChoices(input.outcome),
    };
}

/**
 * Label for an enum value, from `choices`. Falls back to the raw value so an
 * api_type the server knows about but this build has never heard of still
 * renders something meaningful rather than blank.
 */
function labelFor(choices: DigitapChoice[], value: string): string {
    return choices.find((c) => c.value === value)?.label ?? value;
}

function formatTimestamp(iso: string | null): string {
    if (!iso) return EM_DASH;
    const parsed = new Date(iso);
    if (Number.isNaN(parsed.getTime())) return iso;
    return parsed.toLocaleString("en-IN");
}

/**
 * Outcome badge colours. Keyed on the three server-side OUTCOME_CHOICES values;
 * anything unrecognised gets neutral styling rather than being mislabelled as
 * an error.
 */
function outcomeBadgeClass(outcome: string): string {
    switch (outcome) {
        case "success":
            return "bg-green-100 text-green-800 border-green-200";
        case "no_record":
            return "bg-yellow-100 text-yellow-800 border-yellow-200";
        case "error":
            return "bg-red-100 text-red-800 border-red-200";
        default:
            return "bg-gray-100 text-gray-700 border-gray-200";
    }
}

// ── Summary tiles ────────────────────────────────────────────────────────────

const SummaryTile: React.FC<{
    label: string;
    value: number;
    tone?: "neutral" | "success" | "warning" | "danger";
    hint?: string;
}> = ({ label, value, tone = "neutral", hint }) => {
    const toneClass = {
        neutral: "text-gray-900",
        success: "text-green-700",
        warning: "text-yellow-700",
        danger: "text-red-700",
    }[tone];
    return (
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                {label}
            </div>
            <div className={`mt-1 text-2xl font-bold ${toneClass}`}>
                {value.toLocaleString("en-IN")}
            </div>
            {hint && <div className="mt-0.5 text-xs text-gray-400">{hint}</div>}
        </div>
    );
};

/**
 * Aggregate summary. Rendered from `choices` so the tile set matches the
 * server's vocabulary, with a fallback to whatever keys the aggregates
 * themselves carry if `choices` is missing/malformed.
 */
const AggregateSummary: React.FC<{
    aggregates: DigitapLogsAggregates;
    choices: DigitapLogsChoices;
    filtered: boolean;
}> = ({ aggregates, choices, filtered }) => {
    const apiTypeKeys =
        choices.api_type.length > 0
            ? choices.api_type.map((c) => c.value)
            : Object.keys(aggregates.by_api_type).sort();
    const outcomeKeys =
        choices.outcome.length > 0
            ? choices.outcome.map((c) => c.value)
            : Object.keys(aggregates.by_outcome).sort();

    const outcomeTone = (value: string): "success" | "warning" | "danger" | "neutral" => {
        if (value === "success") return "success";
        if (value === "no_record") return "warning";
        if (value === "error") return "danger";
        return "neutral";
    };

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <SummaryTile
                    label="Total Calls"
                    value={aggregates.total}
                    hint={filtered ? "matching the current filters" : "all time"}
                />
                {outcomeKeys.map((value) => (
                    <SummaryTile
                        key={value}
                        label={labelFor(choices.outcome, value)}
                        value={aggregates.by_outcome[value] ?? 0}
                        tone={outcomeTone(value)}
                    />
                ))}
            </div>

            {apiTypeKeys.length > 0 && (
                <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                    <div className="border-b border-gray-100 px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Calls by API Type
                    </div>
                    <div className="divide-y divide-gray-100">
                        {apiTypeKeys.map((apiType) => {
                            const perOutcome = aggregates.by_api_type_outcome[apiType] ?? {};
                            return (
                                <div
                                    key={apiType}
                                    className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 text-sm"
                                >
                                    <span className="font-medium text-gray-800">
                                        {labelFor(choices.api_type, apiType)}
                                    </span>
                                    <span className="font-mono text-xs text-gray-400">
                                        {apiType}
                                    </span>
                                    <span className="ml-auto font-semibold text-gray-900">
                                        {(aggregates.by_api_type[apiType] ?? 0).toLocaleString(
                                            "en-IN",
                                        )}
                                    </span>
                                    <span className="flex shrink-0 gap-1.5">
                                        {outcomeKeys.map((outcome) => {
                                            const n = perOutcome[outcome] ?? 0;
                                            if (n === 0) return null;
                                            return (
                                                <Badge
                                                    key={outcome}
                                                    variant="outline"
                                                    className={`text-[11px] ${outcomeBadgeClass(outcome)}`}
                                                >
                                                    {labelFor(choices.outcome, outcome)}: {n}
                                                </Badge>
                                            );
                                        })}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

// ── Expanded row (on-demand payloads) ────────────────────────────────────────

type PayloadState =
    | { status: "idle" }
    | { status: "loading" }
    | { status: "error"; message: string }
    | {
          status: "ready";
          request_payload: Record<string, unknown> | null;
          response_raw: Record<string, unknown> | null;
      };

/**
 * `<pre>` block for a payload blob.
 *
 * `JSON.stringify` is the ONLY thing done with these objects — they are never
 * logged, never persisted, and never handed to a third-party formatter.
 */
const PayloadBlock: React.FC<{
    title: string;
    warn?: boolean;
    value: Record<string, unknown> | null;
}> = ({ title, warn, value }) => (
    <div>
        <div className="mb-1 font-semibold text-gray-700">
            {title}
            {warn && (
                <span className="ml-1 font-normal text-orange-600">
                    (contains PII — do not copy out of this screen)
                </span>
            )}
        </div>
        <pre
            className={`max-h-60 overflow-y-auto whitespace-pre-wrap break-all rounded border p-2 ${
                warn
                    ? "border-orange-200 bg-orange-50"
                    : "border-gray-200 bg-gray-100"
            }`}
        >
            {value ? JSON.stringify(value, null, 2) : "null"}
        </pre>
    </div>
);

// ── Digitap logs table ───────────────────────────────────────────────────────

const DigitapLogsTable: React.FC<{ onBack: () => void }> = ({ onBack }) => {
    // Committed filter values (what actually goes to the server).
    const [apiType, setApiType] = React.useState("");
    const [outcome, setOutcome] = React.useState("");
    const [userId, setUserId] = React.useState("");
    const [createdFrom, setCreatedFrom] = React.useState("");
    const [createdTo, setCreatedTo] = React.useState("");
    const [search, setSearch] = React.useState("");

    // The raw input value; `search` above trails it by SEARCH_DEBOUNCE_MS.
    const [searchInput, setSearchInput] = React.useState("");

    const [page, setPage] = React.useState(1);
    const [pageSize, setPageSize] = React.useState(PAGE_SIZE_OPTIONS[0]);

    const [data, setData] = React.useState<DigitapLogsResponse | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [loadError, setLoadError] = React.useState<{
        kind: "forbidden" | "validation" | "network" | "unknown";
        message: string;
    } | null>(null);

    // Expansion state. Only ONE row may be expanded at a time: payloads are the
    // PII-heavy part, so the panel deliberately holds at most one row's blobs in
    // memory instead of accumulating them as an operator clicks around.
    const [expandedId, setExpandedId] = React.useState<number | null>(null);
    const [payload, setPayload] = React.useState<PayloadState>({ status: "idle" });

    // Monotonic token, bumped by the Refresh button to force a refetch without
    // having to mutate a filter.
    const [reloadToken, setReloadToken] = React.useState(0);

    /**
     * Which row's payload request is the live one. A ref, not state: it is read
     * inside async callbacks to discard superseded responses, and reading it
     * must never depend on a re-render having happened. Without it, expanding
     * row A then quickly row B could paint A's PII under B's heading.
     */
    const payloadRequestRef = React.useRef<number | null>(null);

    // ── Debounce the search box ──────────────────────────────────────────────
    React.useEffect(() => {
        const timer = window.setTimeout(() => {
            setSearch((current) => (current === searchInput ? current : searchInput));
        }, SEARCH_DEBOUNCE_MS);
        return () => window.clearTimeout(timer);
    }, [searchInput]);

    // Any filter change invalidates the current page number: staying on page 7
    // of a freshly-narrowed result set shows an empty table that looks like
    // "no data" rather than "you scrolled past the end".
    React.useEffect(() => {
        setPage(1);
    }, [apiType, outcome, userId, createdFrom, createdTo, search, pageSize]);

    // ── Fetch the list (never with payloads) ────────────────────────────────
    React.useEffect(() => {
        const controller = new AbortController();
        let active = true;

        setLoading(true);
        // Collapse any expanded row and DROP its payloads: they belong to the
        // previous result set, and keeping PII in state across a query change
        // would outlive the reason it was fetched.
        payloadRequestRef.current = null;
        setExpandedId(null);
        setPayload({ status: "idle" });

        adminService
            .listDigitapLogs(
                {
                    page,
                    page_size: pageSize,
                    ...(apiType ? { api_type: apiType } : {}),
                    ...(outcome ? { outcome } : {}),
                    ...(userId.trim() ? { user_id: userId.trim() } : {}),
                    ...(search ? { search } : {}),
                    ...(createdFrom ? { created_from: createdFrom } : {}),
                    ...(createdTo ? { created_to: createdTo } : {}),
                },
                { signal: controller.signal },
            )
            .then((response) => {
                if (!active) return;
                setData(response.data);
                setLoadError(null);
            })
            .catch((error: unknown) => {
                // An abort is not a failure — it means a newer request replaced
                // this one. Checked FIRST because an abort also has no response
                // and would otherwise be misreported as a network error.
                if (isAdminApiCanceled(error) || !active) return;

                const status = getAdminApiErrorStatus(error);
                if (status === 403) {
                    setLoadError({
                        kind: "forbidden",
                        message: getAdminApiErrorMessage(error, "Forbidden"),
                    });
                } else if (status === 400) {
                    setLoadError({
                        kind: "validation",
                        message: getAdminApiErrorMessage(
                            error,
                            "One of the filters was rejected by the server.",
                        ),
                    });
                } else if (isAdminApiNetworkError(error)) {
                    setLoadError({
                        kind: "network",
                        message:
                            "Could not reach the server. Check your connection and try again.",
                    });
                } else {
                    setLoadError({
                        kind: "unknown",
                        message: getAdminApiErrorMessage(
                            error,
                            "Unable to load Digitap API logs.",
                        ),
                    });
                }
                // A validation error leaves the previous page visible so the
                // offending filter can be corrected in place; every other
                // failure clears the table, because continuing to show rows
                // that no longer correspond to the query would be misleading.
                if (status !== 400) setData(null);
            })
            .finally(() => {
                if (active) setLoading(false);
            });

        return () => {
            active = false;
            controller.abort();
        };
    }, [page, pageSize, apiType, outcome, userId, search, createdFrom, createdTo, reloadToken]);

    // ── On-demand payload fetch for ONE row ─────────────────────────────────
    const toggleRow = React.useCallback(
        (row: DigitapLogRow) => {
            if (expandedId === row.id) {
                // Collapsing drops the payloads from state immediately and
                // invalidates any in-flight fetch for this row.
                payloadRequestRef.current = null;
                setExpandedId(null);
                setPayload({ status: "idle" });
                return;
            }

            payloadRequestRef.current = row.id;
            setExpandedId(row.id);
            setPayload({ status: "loading" });

            // Narrowest query the contract allows: this row's user, this row's
            // api_type, page_size 1 pinned to the row's own timestamp window.
            // `include_payloads=1` is sent ONLY here, never for the list.
            adminService
                .listDigitapLogs({
                    include_payloads: 1,
                    page: 1,
                    page_size: 1,
                    api_type: row.api_type,
                    outcome: row.outcome,
                    ...(row.user_id !== null ? { user_id: String(row.user_id) } : {}),
                    ...(row.created_at
                        ? { created_from: row.created_at, created_to: row.created_at }
                        : {}),
                })
                .then((response) => {
                    // Discard a response whose row is no longer the expanded one:
                    // painting it would put one borrower's PII under another
                    // borrower's heading.
                    if (payloadRequestRef.current !== row.id) return;
                    const match =
                        response.data?.results?.find((r) => r.id === row.id) ?? null;
                    if (!match) {
                        setPayload({
                            status: "error",
                            message:
                                "This log row could not be re-read for payload details. It may have been pruned.",
                        });
                        return;
                    }
                    setPayload({
                        status: "ready",
                        request_payload: match.request_payload ?? null,
                        response_raw: match.response_raw ?? null,
                    });
                })
                .catch((error: unknown) => {
                    if (isAdminApiCanceled(error)) return;
                    if (payloadRequestRef.current !== row.id) return;
                    const status = getAdminApiErrorStatus(error);
                    setPayload({
                        status: "error",
                        message:
                            status === 403
                                ? "Superuser access is required to view request/response payloads."
                                : isAdminApiNetworkError(error)
                                  ? "Could not reach the server to load payload details."
                                  : getAdminApiErrorMessage(
                                        error,
                                        "Unable to load payload details.",
                                    ),
                    });
                });
        },
        [expandedId],
    );

    const aggregates = React.useMemo(
        () => normaliseAggregates(data?.aggregates),
        [data?.aggregates],
    );
    const choices = React.useMemo(() => normaliseChoices(data?.choices), [data?.choices]);

    const rows: DigitapLogRow[] = Array.isArray(data?.results) ? data.results : [];
    const totalPages = typeof data?.total_pages === "number" ? data.total_pages : 0;
    const total = typeof data?.total === "number" ? data.total : 0;
    const maxPageSize =
        typeof data?.max_page_size === "number" && data.max_page_size > 0
            ? data.max_page_size
            : FALLBACK_MAX_PAGE_SIZE;

    const hasFilters = Boolean(
        apiType || outcome || userId.trim() || search || createdFrom || createdTo,
    );

    const resetFilters = () => {
        setApiType("");
        setOutcome("");
        setUserId("");
        setCreatedFrom("");
        setCreatedTo("");
        setSearchInput("");
        setSearch("");
    };

    // Page-size options are clamped to the server's own hard cap, so the UI can
    // never offer a value the backend will silently reduce. If the cap is lower
    // than every option (a future backend tightening), fall back to the cap
    // itself rather than rendering an empty dropdown.
    const allowedPageSizes = PAGE_SIZE_OPTIONS.filter((size) => size <= maxPageSize);
    const pageSizeOptions =
        allowedPageSizes.length > 0 ? allowedPageSizes : [maxPageSize];
    // Keep the current selection representable in the dropdown.
    const pageSizeValue = pageSizeOptions.includes(pageSize)
        ? pageSize
        : pageSizeOptions[0];

    const header = (
        <div className="flex items-center gap-3">
            <Button
                variant="ghost"
                size="icon"
                onClick={onBack}
                className="h-8 w-8 text-gray-500 hover:text-intellend-green"
                title="Back"
            >
                <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
                <h3 className="text-lg font-bold text-gray-900">Digitap API Logs</h3>
                <p className="font-mono text-xs text-gray-400">
                    DigitapAPILog
                    {loadError?.kind === "forbidden"
                        ? ""
                        : ` — ${total.toLocaleString("en-IN")} record${
                              total === 1 ? "" : "s"
                          }${hasFilters ? " (filtered)" : ""}`}
                </p>
            </div>
            <Button
                variant="outline"
                size="icon"
                className="ml-auto h-8 w-8"
                onClick={() => setReloadToken((t) => t + 1)}
                disabled={loading}
                title="Refresh"
            >
                <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
        </div>
    );

    // ── 403: not a superuser ────────────────────────────────────────────────
    // Rendered as its own terminal state, NOT as an empty table: "no records"
    // for an authorisation failure would read as "we made no Digitap calls",
    // which is the opposite of the truth. The backend returns a 403 JSON body
    // (not a 302 to the login page) precisely so this is distinguishable.
    if (loadError?.kind === "forbidden") {
        return (
            <div className="w-full space-y-4">
                {header}
                <div className="w-full rounded-xl border border-amber-200 bg-amber-50 p-8 text-center">
                    <ShieldAlert className="mx-auto h-10 w-10 text-amber-500" />
                    <p className="mt-3 text-sm font-medium text-amber-800">
                        Superuser only.
                    </p>
                    <p className="mx-auto mt-1 max-w-xl text-xs text-amber-700">
                        Digitap API logs expose raw borrower mobile numbers and PANs, so this
                        screen is restricted to superusers. Staff accounts
                        (<span className="font-mono">is_staff</span> without{" "}
                        <span className="font-mono">is_superuser</span>) are deliberately not
                        sufficient. Ask a superuser to review this data for you.
                    </p>
                    <p className="mt-2 font-mono text-[11px] text-amber-600">
                        {loadError.message}
                    </p>
                </div>
            </div>
        );
    }

    // ── Network failure ─────────────────────────────────────────────────────
    if (loadError?.kind === "network") {
        return (
            <div className="w-full space-y-4">
                {header}
                <div className="w-full rounded-xl border border-red-100 bg-red-50 p-8 text-center">
                    <AlertTriangle className="mx-auto h-10 w-10 text-red-400" />
                    <p className="mt-3 text-sm font-medium text-red-700">
                        {loadError.message}
                    </p>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="mt-4"
                        onClick={() => setReloadToken((t) => t + 1)}
                    >
                        <RefreshCw className="mr-2 h-4 w-4" /> Try again
                    </Button>
                </div>
            </div>
        );
    }

    // ── Unexpected failure (5xx, malformed body, …) ──────────────────────────
    if (loadError?.kind === "unknown") {
        return (
            <div className="w-full space-y-4">
                {header}
                <div className="w-full rounded-xl border border-red-100 bg-red-50 p-8 text-center">
                    <AlertTriangle className="mx-auto h-10 w-10 text-red-400" />
                    <p className="mt-3 text-sm font-medium text-red-700">
                        {loadError.message}
                    </p>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="mt-4"
                        onClick={() => setReloadToken((t) => t + 1)}
                    >
                        <RefreshCw className="mr-2 h-4 w-4" /> Try again
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full space-y-4">
            {header}

            <AggregateSummary
                aggregates={aggregates}
                choices={choices}
                filtered={hasFilters}
            />

            {/* Filters. The two Selects are populated from the response's
                `choices` block — never from a hardcoded enum list. Until the
                first response lands they render as disabled placeholders rather
                than as a list this build guessed at. */}
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    <div className="space-y-1.5">
                        <Label htmlFor="dl-api-type">API Type</Label>
                        <Select
                            value={apiType || ALL}
                            onValueChange={(v) => setApiType(v === ALL ? "" : v)}
                            disabled={choices.api_type.length === 0}
                        >
                            <SelectTrigger id="dl-api-type">
                                <SelectValue
                                    placeholder={
                                        choices.api_type.length === 0
                                            ? "Loading…"
                                            : "All Types"
                                    }
                                />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value={ALL}>All Types</SelectItem>
                                {choices.api_type.map((choice) => (
                                    <SelectItem key={choice.value} value={choice.value}>
                                        {choice.label || choice.value}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="space-y-1.5">
                        <Label htmlFor="dl-outcome">Outcome</Label>
                        <Select
                            value={outcome || ALL}
                            onValueChange={(v) => setOutcome(v === ALL ? "" : v)}
                            disabled={choices.outcome.length === 0}
                        >
                            <SelectTrigger id="dl-outcome">
                                <SelectValue
                                    placeholder={
                                        choices.outcome.length === 0
                                            ? "Loading…"
                                            : "All Outcomes"
                                    }
                                />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value={ALL}>All Outcomes</SelectItem>
                                {choices.outcome.map((choice) => (
                                    <SelectItem key={choice.value} value={choice.value}>
                                        {choice.label || choice.value}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="space-y-1.5">
                        <Label htmlFor="dl-user-id">User ID</Label>
                        <Input
                            id="dl-user-id"
                            type="number"
                            min={1}
                            inputMode="numeric"
                            placeholder="e.g. 76"
                            className="font-mono"
                            value={userId}
                            onChange={(e) => setUserId(e.target.value)}
                        />
                    </div>

                    <div className="space-y-1.5">
                        <Label htmlFor="dl-created-from">Created From</Label>
                        <Input
                            id="dl-created-from"
                            type="date"
                            value={createdFrom}
                            onChange={(e) => setCreatedFrom(e.target.value)}
                        />
                    </div>

                    <div className="space-y-1.5">
                        <Label htmlFor="dl-created-to">Created To</Label>
                        <Input
                            id="dl-created-to"
                            type="date"
                            value={createdTo}
                            onChange={(e) => setCreatedTo(e.target.value)}
                        />
                    </div>

                    <div className="space-y-1.5">
                        <Label htmlFor="dl-search">Search</Label>
                        <Input
                            id="dl-search"
                            type="search"
                            placeholder="username or error detail"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                        />
                        <p className="text-xs text-gray-400">
                            Matches username or error detail. Applied automatically as you
                            type.
                        </p>
                    </div>
                </div>

                {hasFilters && (
                    <div className="mt-3 flex justify-end">
                        <Button variant="outline" size="sm" onClick={resetFilters}>
                            Clear filters
                        </Button>
                    </div>
                )}
            </div>

            {/* 400 — the server rejected a filter value. Shown INLINE with the
                filter controls still mounted, because the fix is to correct the
                input, and an empty table here would misreport a typo'd date as
                "no Digitap calls". */}
            {loadError?.kind === "validation" && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                    <span className="font-semibold">Invalid filter:</span>{" "}
                    {loadError.message}
                </div>
            )}

            {/* Table. During a refetch the previous rows stay mounted but are
                dimmed and overlaid with a spinner — stale data is never shown
                as if it were current. */}
            <div className="relative overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                {loading && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60">
                        <span className="flex items-center gap-2 text-sm font-medium text-gray-600">
                            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
                        </span>
                    </div>
                )}
                <div className={loading ? "opacity-50" : undefined}>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="w-40">Time</TableHead>
                                <TableHead>User</TableHead>
                                <TableHead>API Type</TableHead>
                                <TableHead>Outcome</TableHead>
                                <TableHead className="w-24">Result Code</TableHead>
                                <TableHead>Error Detail</TableHead>
                                <TableHead className="w-28">Payloads</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {rows.length === 0 && !loading && (
                                <TableRow>
                                    <TableCell
                                        colSpan={7}
                                        className="py-10 text-center text-sm text-gray-500"
                                    >
                                        {hasFilters ? (
                                            <>
                                                No Digitap calls match these filters.
                                                <div className="mt-2">
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={resetFilters}
                                                    >
                                                        Clear filters
                                                    </Button>
                                                </div>
                                            </>
                                        ) : (
                                            "No Digitap API calls have been logged yet."
                                        )}
                                    </TableCell>
                                </TableRow>
                            )}
                            {rows.map((row) => {
                                const isExpanded = expandedId === row.id;
                                return (
                                    <React.Fragment key={row.id}>
                                        <TableRow className="hover:bg-gray-50">
                                            <TableCell className="text-xs text-gray-600">
                                                {formatTimestamp(row.created_at)}
                                            </TableCell>
                                            <TableCell className="text-xs">
                                                <div className="font-medium">
                                                    {row.username ?? EM_DASH}
                                                </div>
                                                <div className="text-gray-400">
                                                    ID: {row.user_id ?? EM_DASH}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <Badge
                                                    variant="outline"
                                                    className="text-xs"
                                                    title={row.api_type}
                                                >
                                                    {labelFor(choices.api_type, row.api_type)}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                <Badge
                                                    className={`text-xs ${outcomeBadgeClass(row.outcome)}`}
                                                >
                                                    {labelFor(choices.outcome, row.outcome)}
                                                </Badge>
                                            </TableCell>
                                            <TableCell className="font-mono text-xs">
                                                {row.result_code ?? EM_DASH}
                                            </TableCell>
                                            <TableCell className="max-w-xs truncate text-xs text-gray-600">
                                                {row.error_detail || EM_DASH}
                                            </TableCell>
                                            <TableCell>
                                                {/* Deliberate, labelled action rather than a
                                                    bare chevron: clicking it fetches raw
                                                    mobile/PAN data for this one row. */}
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    className="h-7 gap-1 px-2 text-xs"
                                                    onClick={() => toggleRow(row)}
                                                    title={
                                                        isExpanded
                                                            ? "Hide payloads"
                                                            : "Reveal request/response payloads (contains PII)"
                                                    }
                                                >
                                                    {isExpanded ? (
                                                        <>
                                                            <ChevronUp className="h-3 w-3" />
                                                            Hide
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Eye className="h-3 w-3" />
                                                            Reveal
                                                        </>
                                                    )}
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                        {isExpanded && (
                                            <TableRow>
                                                <TableCell colSpan={7} className="bg-gray-50 p-4">
                                                    <div className="space-y-3 text-xs">
                                                        {row.error_detail && (
                                                            <div>
                                                                <div className="mb-1 font-semibold text-red-700">
                                                                    Error Detail
                                                                </div>
                                                                <pre className="whitespace-pre-wrap break-all rounded border border-red-200 bg-red-50 p-2">
                                                                    {row.error_detail}
                                                                </pre>
                                                            </div>
                                                        )}
                                                        {payload.status === "loading" && (
                                                            <div className="flex items-center gap-2 text-gray-500">
                                                                <Loader2 className="h-3 w-3 animate-spin" />
                                                                Fetching payloads…
                                                            </div>
                                                        )}
                                                        {payload.status === "error" && (
                                                            <div className="rounded border border-red-200 bg-red-50 p-2 text-red-800">
                                                                {payload.message}
                                                            </div>
                                                        )}
                                                        {payload.status === "ready" && (
                                                            <>
                                                                <PayloadBlock
                                                                    title="Request Payload"
                                                                    warn
                                                                    value={payload.request_payload}
                                                                />
                                                                <PayloadBlock
                                                                    title="Response Raw"
                                                                    warn
                                                                    value={payload.response_raw}
                                                                />
                                                            </>
                                                        )}
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </TableBody>
                    </Table>
                </div>
            </div>

            {/* Pagination. `total_pages` is the server's own figure, so the
                Next button cannot walk past the end even if `page_size` was
                clamped server-side. */}
            {totalPages > 0 && (
                <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-gray-500">
                    <span>
                        Page {data?.page ?? page} of {totalPages} (
                        {total.toLocaleString("en-IN")} records)
                    </span>
                    <div className="flex items-center gap-2">
                        <Label htmlFor="dl-page-size" className="text-xs text-gray-500">
                            Rows
                        </Label>
                        <Select
                            value={String(pageSizeValue)}
                            onValueChange={(v) => setPageSize(Number(v))}
                        >
                            <SelectTrigger id="dl-page-size" className="h-8 w-20">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {pageSizeOptions.map((size) => (
                                    <SelectItem key={size} value={String(size)}>
                                        {size}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={loading || page <= 1}
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                        >
                            Previous
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={loading || page >= totalPages}
                            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        >
                            Next
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};

// ── Panel shell ──────────────────────────────────────────────────────────────

/**
 * v2's shell: a launcher list with one tool ("Digitap") behind which the log
 * table opens. Kept verbatim in structure, because the whole point of the shell
 * is that further billed vendors (Digio, Perfios, …) drop in as extra rows
 * without restructuring the screen.
 */
type ExpensesTool = "digitap";

const ExpensesPanel: React.FC = () => {
    const [activeTool, setActiveTool] = React.useState<ExpensesTool | null>(null);

    if (activeTool === "digitap") {
        return (
            <div className="w-full">
                <DigitapLogsTable onBack={() => setActiveTool(null)} />
            </div>
        );
    }

    return (
        <div className="w-full max-w-3xl space-y-8">
            <div>
                <h2 className="text-2xl font-bold text-gray-900">Expenses</h2>
                <p className="mt-1 text-sm text-gray-500">
                    Track external vendor API usage. Digitap bills per call, so call
                    volume is the spend figure.
                </p>
            </div>
            <div className="space-y-3">
                <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-500">
                    API Logs
                </h3>
                <Button
                    variant="outline"
                    className="h-auto min-h-[4rem] w-full min-w-0 justify-start gap-3 border-intellend-green/20 py-3 text-base hover:border-intellend-green hover:bg-intellend-green/5 sm:gap-4 sm:text-lg"
                    onClick={() => setActiveTool("digitap")}
                >
                    <div className="flex w-full items-center overflow-hidden">
                        <Activity className="h-5 w-5 shrink-0 text-intellend-green sm:h-6 sm:w-6" />
                        <div className="ml-3 min-w-0 flex-1 overflow-hidden text-left sm:ml-4">
                            <div className="truncate font-semibold">Digitap</div>
                            <div className="truncate text-xs font-normal text-gray-500 sm:text-sm">
                                Call volume, outcomes and failures by API type
                            </div>
                        </div>
                        <ChevronRight className="ml-auto h-4 w-4 shrink-0 text-gray-400" />
                    </div>
                </Button>
            </div>
        </div>
    );
};

export default ExpensesPanel;